import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { usePricingAccess } from '@/hooks/usePricingAccess';
import { useAuth } from '@/hooks/useAuth';
import { useToast } from '@/hooks/use-toast';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { ArrowLeft, Sparkles, ChefHat, Droplets, Home, Utensils, Package, Hand, Crown, Clock, CheckSquare, Filter, Wine, Shield, UtensilsCrossed, Users, Trees, Building, Sprout, Settings, Waves } from 'lucide-react';
import { LayoutWrapper } from '@/components/LayoutWrapper';

interface CrystalTemplate {
  id: string;
  area_name: string;
  area_description: string;
  template_items: any[];
  frequency: string;
  category: string;
  icon_name: string;
  sort_order: number;
  template_level?: 'basic' | 'comprehensive';
}

const getIconComponent = (iconName: string) => {
  const icons = {
    ChefHat,
    Droplets,
    Home,
    Utensils,
    Package,
    Hand,
    Wine,
    Shield,
    UtensilsCrossed,
    Users,
    Trees,
    Building,
    Sprout,
    Settings,
    Waves,
  };
  return icons[iconName as keyof typeof icons] || Utensils;
};

export const CrystalTemplates = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { hasFeature, getUpgradeMessage, tenant } = usePricingAccess();
  const { toast } = useToast();
  const [isCreating, setIsCreating] = useState<string | null>(null);
  const [selectedCategory, setSelectedCategory] = useState<string>('all');

  // Fetch Crystal HACCP templates based on user's plan
  const { data: allTemplates, isLoading } = useQuery({
    queryKey: ['crystal-templates'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('crystal_haccp_templates')
        .select('*')
        .eq('is_active', true)
        .order('sort_order', { ascending: true });

      if (error) throw error;
      return data as CrystalTemplate[];
    },
  });

  // Filter templates based on user's access level
  const templates = React.useMemo(() => {
    if (!allTemplates) return [];
    
    if (hasFeature('crystalTemplatesFull')) {
      // Pro users see all templates
      return allTemplates;
    } else if (hasFeature('crystalTemplatesBasic')) {
      // Starter/Growth users see only basic templates
      return allTemplates.filter(template => 
        template.template_level === 'basic'
      );
    }
    
    return [];
  }, [allTemplates, hasFeature]);

  const tenantId = tenant?.id;

  // Filter templates by category
  const filteredTemplates = templates ? templates.filter(template => 
    selectedCategory === 'all' || template.category === selectedCategory
  ) : [];

  // Get unique categories
  const categories = templates ? ['all', ...Array.from(new Set(templates.map(t => t.category)))] : ['all'];

  const getCategoryLabel = (category: string) => {
    switch (category) {
      case 'all': return 'All Templates';
      case 'cleaning': return 'Kitchen & Cleaning';
      case 'bar': return 'Bar Operations';
      case 'restaurant': return 'Restaurant Service';
      case 'external': return 'External Areas';
      default: return category.charAt(0).toUpperCase() + category.slice(1);
    }
  };

  const getCategoryDescription = (category: string) => {
    const isBasicUser = !hasFeature('crystalTemplatesFull') && hasFeature('crystalTemplatesBasic');
    
    switch (category) {
      case 'all': return isBasicUser 
        ? 'Browse all 4 essential HACCP templates with 10 core items each'
        : 'Browse all available Crystal HACCP templates with 15+ comprehensive items each';
      case 'cleaning': return isBasicUser
        ? 'Essential kitchen cleaning and hygiene procedures'
        : 'Kitchen equipment, sanitization, and cleaning protocols with detailed procedures';
      case 'bar': return isBasicUser
        ? 'Essential bar maintenance and safety checks'
        : 'Bar equipment, liquor storage, and beverage service comprehensive checklists';
      case 'restaurant': return isBasicUser
        ? 'Essential front-of-house service area checks'
        : 'Dining area, front of house, and customer service operational procedures';
      case 'external': return 'Outdoor dining, building exterior, and external maintenance protocols';
      default: return `${category} related templates`;
    }
  };

  const getCategoryIcon = (category: string) => {
    switch (category) {
      case 'cleaning': return ChefHat;
      case 'bar': return Wine;
      case 'restaurant': return UtensilsCrossed;
      case 'external': return Trees;
      default: return Sparkles;
    }
  };

  const handleUseTemplate = async (template: CrystalTemplate) => {
    if (!hasFeature('crystalTemplatesBasic')) {
      toast({
        title: "Premium Feature",
        description: "Upgrade to access Crystal HACCP templates",
        variant: "destructive",
      });
      return;
    }

    if (!tenantId) {
      toast({
        title: "Error",
        description: "Unable to determine your tenant. Please try again.",
        variant: "destructive",
      });
      return;
    }

    setIsCreating(template.id);

    try {
      const { data, error } = await supabase
        .from('checklist_templates')
        .insert({
          tenant_id: tenantId,
          name: `${template.area_name} - Crystal HACCP`,
          description: template.area_description,
          frequency: template.frequency,
          template_category: 'crystal_haccp',
          items: template.template_items,
          is_system_template: false,
        })
        .select()
        .single();

      if (error) throw error;

      toast({
        title: "Template Created",
        description: `${template.area_name} template has been added to your templates.`,
      });

      navigate('/templates');
    } catch (error) {
      console.error('Error creating template:', error);
      toast({
        title: "Error",
        description: "Failed to create template. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsCreating(null);
    }
  };

  if (isLoading) {
    return (
      <LayoutWrapper>
        <div className="container mx-auto px-4 py-8">
          <div className="flex items-center gap-4 mb-8">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate('/dashboard')}
              className="flex items-center gap-2"
            >
              <ArrowLeft className="h-4 w-4" />
              Back to Dashboard
            </Button>
          </div>
          
          <div className="text-center mb-8">
            <div className="flex items-center justify-center gap-2 mb-4">
              <Sparkles className="h-8 w-8 text-primary" />
              <h1 className="text-3xl font-bold">Crystal HACCP Templates</h1>
              <Crown className="h-6 w-6 text-primary" />
            </div>
          </div>
          
          <Tabs value="all" className="w-full mb-6">
            <TabsList className="grid w-full grid-cols-5">
              <TabsTrigger value="all">All</TabsTrigger>
              <TabsTrigger value="cleaning">Kitchen</TabsTrigger>
              <TabsTrigger value="bar">Bar</TabsTrigger>
              <TabsTrigger value="restaurant">Restaurant</TabsTrigger>
              <TabsTrigger value="external">External</TabsTrigger>
            </TabsList>
          </Tabs>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[...Array(9)].map((_, i) => (
              <Card key={i} className="animate-pulse">
                <CardHeader>
                  <div className="h-6 bg-muted rounded w-3/4"></div>
                  <div className="h-4 bg-muted rounded w-full"></div>
                </CardHeader>
                <CardContent>
                  <div className="h-10 bg-muted rounded"></div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </LayoutWrapper>
    );
  }

  const canAccessFeature = hasFeature('crystalTemplatesBasic');

  return (
    <LayoutWrapper>
      <main className="container mx-auto px-4 py-8">
        <div className="flex items-center gap-4 mb-8">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate('/dashboard')}
            className="flex items-center gap-2"
          >
            <ArrowLeft className="h-4 w-4" />
            Back to Dashboard
          </Button>
        </div>

        <div className="text-center mb-8">
          <div className="flex items-center justify-center gap-2 mb-4">
            <Sparkles className="h-8 w-8 text-primary" />
            <h1 className="text-3xl font-bold">
              {hasFeature('crystalTemplatesFull') ? 'Crystal HACCP Templates' : 'Essential HACCP Templates'}
            </h1>
            <Crown className="h-6 w-6 text-primary" />
          </div>
          <p className="text-muted-foreground max-w-2xl mx-auto mb-4">
            {hasFeature('crystalTemplatesFull') 
              ? 'Professional templates for kitchen, bar, restaurant, and external areas. Each template contains 15+ comprehensive checklist items with detailed procedures.'
              : 'Essential beginner-friendly templates to get you started. Each template contains 10 core items covering the most critical checks.'
            }
          </p>
          
          {/* Upgrade banner for basic users */}
          {!hasFeature('crystalTemplatesFull') && hasFeature('crystalTemplatesBasic') && (
            <div className="bg-gradient-to-r from-primary/10 to-primary/5 border border-primary/20 rounded-lg p-4 mb-6 max-w-3xl mx-auto">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="h-10 w-10 rounded-full bg-primary/20 flex items-center justify-center">
                    <Crown className="h-5 w-5 text-primary" />
                  </div>
                  <div className="text-left">
                    <h3 className="font-semibold text-foreground">Essential Templates</h3>
                    <p className="text-sm text-muted-foreground">
                      You have access to 4 essential templates with 10 core items each. 
                      <span className="font-medium text-primary"> Upgrade to Pro</span> to unlock the full library with 15+ comprehensive items per template.
                    </p>
                  </div>
                </div>
                <Button 
                  onClick={() => navigate('/pricing')}
                  size="sm"
                  className="bg-primary hover:bg-primary/90 whitespace-nowrap"
                >
                  Upgrade Now
                </Button>
              </div>
            </div>
          )}
          
          {!hasFeature('crystalTemplatesBasic') && (
            <div className="mb-6 p-4 bg-muted rounded-lg border-2 border-dashed max-w-md mx-auto">
              <h3 className="font-semibold mb-2 flex items-center gap-2 justify-center">
                <Crown className="h-4 w-4 text-primary" />
                Premium Feature
              </h3>
              <p className="text-sm text-muted-foreground mb-3">
                Crystal HACCP Templates are available for Starter plans and higher. Upgrade to access our essential collection.
              </p>
              <Button onClick={() => navigate('/pricing')} size="sm">
                Upgrade Plan
              </Button>
            </div>
          )}
        </div>
        
        <Tabs value={selectedCategory} onValueChange={setSelectedCategory} className="w-full">
          <TabsList className="grid w-full grid-cols-5 mb-6">
            {categories.map((category) => {
              const CategoryIcon = getCategoryIcon(category);
              return (
                <TabsTrigger key={category} value={category} className="text-xs sm:text-sm flex items-center gap-1">
                  <CategoryIcon className="h-3 w-3 sm:h-4 sm:w-4" />
                  <span className="hidden sm:inline">{getCategoryLabel(category).split(' ')[0]}</span>
                  <span className="sm:hidden">{getCategoryLabel(category).split(' ')[0].slice(0, 3)}</span>
                </TabsTrigger>
              );
            })}
          </TabsList>
          
          <div className="mb-6">
            <h2 className="text-xl font-semibold mb-1 flex items-center gap-2">
              {getCategoryLabel(selectedCategory)}
              {selectedCategory !== 'all' && (
                <Badge variant="secondary" className="text-xs">
                  {filteredTemplates.length} templates
                </Badge>
              )}
            </h2>
            <p className="text-sm text-muted-foreground">{getCategoryDescription(selectedCategory)}</p>
          </div>
          
          <div className={`grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 ${!hasFeature('crystalTemplatesBasic') ? 'opacity-50 pointer-events-none' : ''}`}>
            {filteredTemplates.map((template) => {
              const IconComponent = getIconComponent(template.icon_name);
              const itemCount = Array.isArray(template.template_items) ? template.template_items.length : 0;
              
              return (
                <Card 
                  key={template.id} 
                  className="relative transition-all duration-200 hover:shadow-lg hover:scale-[1.02]"
                >
                  {!hasFeature('crystalTemplatesBasic') && (
                    <div className="absolute top-2 right-2 z-10">
                      <Crown className="h-5 w-5 text-primary" />
                    </div>
                  )}
                  <CardHeader className="pb-4">
                    <div className="flex items-center gap-3">
                      <div className="p-2 rounded-lg bg-primary/10">
                        <IconComponent className="h-6 w-6 text-primary" />
                      </div>
                      <div className="flex-1">
                        <CardTitle className="text-lg leading-tight">{template.area_name}</CardTitle>
                      </div>
                    </div>
                    <CardDescription className="line-clamp-2 text-sm mt-2">
                      {template.area_description}
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="pt-0">
                    <div className="flex items-center gap-4 mb-4 text-sm text-muted-foreground">
                      <div className="flex items-center gap-1">
                        <Clock className="w-4 h-4" />
                        <span className="capitalize">{template.frequency}</span>
                      </div>
                      <div className="flex items-center gap-1">
                        <CheckSquare className="w-4 h-4" />
                        <span className="font-medium text-primary">{itemCount} items</span>
                      </div>
                    </div>
                    
                    <div className="mb-4 flex items-center gap-2">
                      <Badge variant="secondary" className="text-xs capitalize">
                        {template.category.replace('_', ' ')}
                      </Badge>
                      {template.template_level === 'basic' && (
                        <Badge variant="outline" className="text-xs text-primary border-primary/30">
                          Essential
                        </Badge>
                      )}
                      {template.template_level === 'comprehensive' && (
                        <Badge variant="default" className="text-xs bg-primary/10 text-primary">
                          Comprehensive
                        </Badge>
                      )}
                    </div>
                    
                    <Button
                      onClick={() => handleUseTemplate(template)}
                      disabled={isCreating === template.id || !hasFeature('crystalTemplatesBasic')}
                      className="w-full"
                    >
                      {isCreating === template.id ? "Creating..." : "Use This Template"}
                    </Button>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </Tabs>
        
        {filteredTemplates.length === 0 && templates && templates.length > 0 && (
          <div className="text-center py-12">
            <Filter className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
            <h3 className="text-lg font-semibold mb-2">No templates in this category</h3>
            <p className="text-muted-foreground">
              Try selecting a different category to see available templates.
            </p>
          </div>
        )}
        
        {(!templates || templates.length === 0) && (
          <div className="text-center py-12">
            <h3 className="text-lg font-semibold mb-2">No templates found</h3>
            <p className="text-muted-foreground">
              Crystal HACCP templates will be available soon.
            </p>
          </div>
        )}

        {!hasFeature('crystalTemplatesBasic') && (
          <div className="mt-12 text-center">
            <Card className="max-w-md mx-auto">
              <CardHeader>
                <div className="flex items-center justify-center gap-2 mb-2">
                  <Crown className="h-6 w-6 text-primary" />
                  <CardTitle>Upgrade to Access Templates</CardTitle>
                </div>
                <CardDescription>
                  Crystal HACCP Templates are available with Starter plan or higher
                </CardDescription>
              </CardHeader>
              <CardContent>
                <Button 
                  onClick={() => navigate('/pricing')}
                  className="w-full"
                >
                  View Pricing Plans
                </Button>
              </CardContent>
            </Card>
          </div>
        )}
      </main>
    </LayoutWrapper>
  );
};